import isUndefined from 'lodash/isUndefined';
import findIndex from 'lodash/findIndex';
import { Column, ColumnState, GridApi, IServerSideGetRowsRequest } from '@ag-grid-community/core';

import { removeNullGroupKeys } from './TableDataHelpers';
import { Hierarchy, HierarchyColumnConfig, OrderConfig } from 'common/visualizations/vif';
import { FeatureFlags } from 'common/feature_flags';
import { PIVOT_SEPARATOR } from '../views/agGridReact/Constants';
import { createPivotResultColumns } from './ComputePivotColumn';
import { GrandTotalRow, GrandTotalRowEntry } from 'common/types/agGrid/grandTotal';

/**
 * Extract pivot column order from column state
 */
export function extractPivotColumnOrder(columnState: ColumnState[]): string[] {
  const pivotColumns = columnState
    .filter((col) => col.colId && col.colId.includes(PIVOT_SEPARATOR))
    .map((col) => {
      // Extract the pivot part (everything before the last @@)
      const parts = col.colId!.split(PIVOT_SEPARATOR);
      return parts.slice(0, -1).join(PIVOT_SEPARATOR);
    });

  // Remove duplicates while preserving order
  return [...new Set(pivotColumns)];
}

/**
 * Extract pivot column widths from columns
 */
export function extractPivotColumnWidths(columns: Column[]): { [columnId: string]: number } {
  const pivotColumnWidths: { [columnId: string]: number } = {};

  columns.forEach((column) => {
    const colId = column.getColId();
    if (colId && colId.includes(PIVOT_SEPARATOR)) {
      pivotColumnWidths[colId] = column.getActualWidth();
    }
  });

  return pivotColumnWidths;
}

/**
 * Apply stored pivot column widths to the grid
 */
export function applyPivotColumnWidths(api: GridApi, pivotColumnWidths: { [columnId: string]: number }) {
  const columnWidths = Object.entries(pivotColumnWidths).map(([colId, width]) => ({
    key: colId,
    newWidth: width,
  }));

  if (columnWidths.length > 0) {
    // Use setTimeout to ensure columns exist before applying widths
    setTimeout(() => {
      api.setColumnWidths(columnWidths);
    }, 0);
  }
}

/**
 * Fudges pivot behavior by pivoting the data client-side.
 * This may be a permanent solution, as it never has to handle more than ~1000ish rows.
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {Array} rows - The rows of data to pivot, returned by SoQL.
 * @returns {Object} - The pivoted rows and list of pivoted fields.
 */
export const doClientSidePivot = (
  agRequest: IServerSideGetRowsRequest,
  rows: any[],
  customPivotOrder?: string[],
): { pivotedRows: any[]; pivotFields: string[] } => {
  const { rowGroupCols, groupKeys: agGroupKeys, valueCols, pivotCols } = agRequest;
  const groupKeys = removeNullGroupKeys(agGroupKeys);

  const pivotFields: string[] = [];
  valueCols.forEach((valueCol) => {
    const valCol = valueCol.id;
    pivotFields.push(
      ...new Set(
        rows.map((row) => {
          const pivKeys = pivotCols.map((pivotCol) => row[pivotCol.id]); // Use all pivot columns
          return [...pivKeys, valCol].join(PIVOT_SEPARATOR); // Concatenate the value column
        }),
      ),
    );
  });

  // For each group, we create a row, or get it if it already exists
  // then add the pivoted data to that row.
  // If we're not grouping, then there's only one row.
  // Example:
  // This data:
  // [
  //   { the_group: '2019', the_pivot: 'Smith', the_value: '272' },
  //   { the_group: '2019', the_pivot: 'Jones', the_value: '554' }
  // ]
  // Becomes: [{ 'the_group': '2019', 'Smith@@the_value': 272, 'Jones@@the_value': 554 }]
  const groCol = rowGroupCols.length > 0 ? rowGroupCols[groupKeys.length].id : null;
  const rowIndexMap = {} as any;

  const getCurrentRow = (acc: any[], row: any) => {
    // Case 1: no grouping - all pivot data goes in the same/first row
    if (groCol === null) {
      if (acc.length === 0) {
        acc.push({});
      }
      return acc[0];
    }

    // Case 2: grouping - use a separate row for each group
    const groValue = row[groCol];
    if (isUndefined(rowIndexMap[groValue])) {
      rowIndexMap[groValue] = acc.length;
      acc.push({ [groCol]: groValue });
    }
    return acc[rowIndexMap[groValue]];
  };

  const pivotedRows = rows.reduce((acc: any, row: any) => {
    const curRow = getCurrentRow(acc, row);
    valueCols.forEach((valueCol) => {
      const valCol = valueCol.id;
      // Construct the row identifier by joining all pivot columns and concatenating the value column
      const separatedRowName = [...pivotCols.map((pivotCol) => row[pivotCol.id]), valCol].join(
        PIVOT_SEPARATOR,
      );
      curRow[separatedRowName] = row[valCol];
    });

    return acc;
  }, []);

  // The pivot results columns are displayed in the order provided in PivotFields.
  if (customPivotOrder && customPivotOrder.length > 0) {
    // Use custom pivot order if provided
    pivotFields.sort((a, b) => {
      //Extract the pivot column names from the field names
      const aPivotPart = a.split(PIVOT_SEPARATOR).slice(0, -1).join(PIVOT_SEPARATOR);
      const bPivotPart = b.split(PIVOT_SEPARATOR).slice(0, -1).join(PIVOT_SEPARATOR);

      const aIndex = customPivotOrder.indexOf(aPivotPart);
      const bIndex = customPivotOrder.indexOf(bPivotPart);

      // If both parts are found in the custom order, sort by their indices
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one part is found, it should come first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // If neither part is found,fall back to alphabetical order
      return a.localeCompare(b);
    });
  } else {
    // Default behavior: sort alphabetically
    pivotFields.sort();
  }
  return { pivotedRows, pivotFields };
};

/**
 * Adds pivot result column fieldnames to the grid using the AgGrid API
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {Array} pivotFields - The pivot results columns used to generate the data.
 * @param {GridApi} api - The Ag Grid API.
 */
export function addPivotResultCols(
  agRequest: IServerSideGetRowsRequest,
  pivotFields: string[],
  api: GridApi,
  datasetUid: string,
) {
  if (agRequest.groupKeys.length !== 0 || agRequest.startRow !== 0) {
    // If this is just partial data because we are expanding a group or turning on subtotals,
    // don't change the pivot result columns
    return;
  }
  // Create pivot colDef's based of data returned from the server
  const pivotResultColumns = createPivotResultColumns(agRequest, pivotFields, datasetUid);
  // Supply pivot result columns to the grid
  api.setPivotResultColumns(pivotResultColumns);
}

/**
 * Clears any pivot result columns fieldnames that were set
 * @param {GridApi} api - The Ag Grid API.
 */
export function clearPivotResultCols(api: GridApi) {
  api.setPivotResultColumns(null);
}

/**
 * Sort the hierarchyColumnConfigurations into:
 * 1. Group columns first in sorted order
 * 2. Pivot columns second in sorted order
 * 3. All other columns
 *
 * Use the rowGroupIndex and pivotIndex in columnState to determine the sort order.
 */
export function sortHierarchyColumnConfigurations(
  columnConfigs: HierarchyColumnConfig[],
  columnState: ColumnState[],
) {
  // Helper function to get the sorting index for a given column type (rowGroup or pivot)
  const getSortIndex = (config: HierarchyColumnConfig, type: 'rowGroup' | 'pivot') => {
    const indexKey = type === 'rowGroup' ? 'rowGroupIndex' : 'pivotIndex';
    const index = findIndex(columnState, { colId: config.columnName });
    return columnState[index][indexKey] ?? 0;
  };

  // Sort function to sort columns based on a provided type (rowGroup or pivot)
  const sortColumns = (columns: HierarchyColumnConfig[], type: 'rowGroup' | 'pivot') =>
    columns.sort((a, b) => getSortIndex(a, type) - getSortIndex(b, type));

  // Separate columns into their respective categories
  const groupingColumns = columnConfigs.filter((config) => config.isGrouping);
  const pivotingColumns = columnConfigs.filter((config) => config.isPivoting);
  const otherColumns = columnConfigs.filter((config) => !config.isGrouping && !config.isPivoting);

  // Sort grouping and pivoting columns
  const sortedGroupingColumns = sortColumns(groupingColumns, 'rowGroup');
  const sortedPivotingColumns = sortColumns(pivotingColumns, 'pivot');

  // Return sorted columns with grouping columns (in order), followed by pivot columns (in order),
  // followed by all other columns.
  return [...sortedGroupingColumns, ...sortedPivotingColumns, ...otherColumns];
}

/**
 * Remove sorting from any columns that are not row groups columns. When pivoting, you can only
 * have sorting on row group columns.
 */
export function removeSortingFromNonRowGroupColumns(hierarchy: Hierarchy): OrderConfig[] | undefined {
  const rowGroupColumnNames: string[] = [];
  hierarchy.columnConfigurations.forEach((column) => {
    if (column.isGrouping === true) {
      rowGroupColumnNames.push(column.columnName);
    }
  });
  return hierarchy.order?.filter((column) => rowGroupColumnNames.includes(column.columnName));
}

/**
 * Given a row of data from the query, construct a pivot column name for AgGrid.
 */
function constructPivotColumnName(row: GrandTotalRowEntry, pivotKeys: string[]) {
  let columnName = row[pivotKeys[0]];
  const keys = pivotKeys.slice(1);
  keys.forEach((key) => (columnName += `${PIVOT_SEPARATOR}${row[key]}`));
  return columnName;
}

/**
 * Given rows of data from the query, construct grand totals for pivot that we pass to AgGrid.
 * Here are some sample rows from different queries:
 *  {gold: '32', sport: 'Archery'}
 *  {gold: '63', sport: 'Athletics', year: '2008'}
 *  {gold: '253', silver: '257', sport: 'Athletics'}
 *
 * The format we return should be: [{Athletics@@gold: '253', Archery@@gold: '32'}] where sport is the pivot
 * column, gold is the value (aggregation), and the number is the grand total to display for that
 * pivot column.
 * For multiple pivot columns, the format should be [{Athletics@@2008@@gold: 63, Athletics@@2012@@gold: 64}]
 * For multiple value columns, the format should be [{Athletics@@gold: 253, Athletics@@silver: 257}]
 */
export const constructPivotGrandTotal = (
  row: GrandTotalRow,
  hierarchy: Hierarchy,
): Record<string, string>[] => {
  const result: Record<string, string> = {};
  if (row.length === 0) {
    return [];
  }

  const configs = hierarchy?.columnConfigurations;
  const pivotConfigs = configs.filter((config) => config.isPivoting);
  const pivotCols = pivotConfigs.map((col) => col.columnName);
  // Given a row of data, determine which keys are pivot keys and which are value keys
  const allKeys = Object.keys(row[0]);
  const pivotKeys = allKeys.filter((key) => pivotCols.includes(key));
  const valueKeys = allKeys.filter((key) => !pivotCols.includes(key));

  row.forEach((entry) => {
    const pivotColumnName = constructPivotColumnName(entry, pivotKeys);
    valueKeys.forEach((valueKey) => {
      result[`${pivotColumnName}${PIVOT_SEPARATOR}${valueKey}`] = entry[valueKey];
    });
  });

  // Account for column names with spaces as keys.
  return [JSON.parse(JSON.stringify(result))];
};

/**
 * Pivot is allowed if pivot mode is toggled on and both pivot columns and value columns are defined
 */
export function isPivotInAgRequest(agRequest: IServerSideGetRowsRequest): boolean {
  return (
    FeatureFlags.valueOrDefault('enable_pivot_tables_in_ax', false) &&
    agRequest.pivotMode &&
    agRequest.pivotCols.length > 0 &&
    agRequest.valueCols.length > 0
  );
}

/**
 * Pivot is defined in the GridApi if pivot mode is toggled on and both pivot columns and value columns are defined
 */
export function isPivotInGridApi(gridApi: GridApi): boolean {
  return (
    FeatureFlags.valueOrDefault('enable_pivot_tables_in_ax', false) &&
    gridApi.isPivotMode() &&
    gridApi.getPivotColumns().length > 0 &&
    gridApi.getValueColumns().length > 0
  );
}

/**
 * Pivot is in the hierarchy if pivot mode is toggled on and both pivot columns and value columns are defined
 */
export function isPivotInHierarchy(hierarchy: Hierarchy): boolean {
  return (
    FeatureFlags.valueOrDefault('enable_pivot_tables_in_ax', false) &&
    hierarchy?.pivotMode &&
    hierarchy?.columnConfigurations?.filter((config) => config?.isPivoting)?.length > 0 &&
    hierarchy?.columnConfigurations?.filter((config) => config?.aggregation)?.length > 0
  );
}

export function isPivotFeatureFlagOn(): boolean {
  return FeatureFlags.valueOrDefault('enable_pivot_tables_in_ax', false);
}
